import { Css, Palette, Tooltip, useTestIds } from "@homebound/beam";
import { useMemo } from "react";
import { useParams } from "react-router";
import { Icon } from "src/components";
import { PieChart } from "src/components/charts/PieChart";
import {
  CompletedTaskBudgetReviewWidgetQuery,
  useCompletedTaskBudgetReviewWidgetQuery,
} from "src/routes/projects/budget/components/reports/widgets/CompletedTaskBudgetReviewWidget.generated";
import { ProjectParams } from "src/routes/routesDef";
import { formatCentsToPrice } from "src/utils";

export function CompletedTaskBudgetReviewWidget() {
  const tid = useTestIds({});
  const { projectId } = useParams<ProjectParams>();
  const { data, loading } = useCompletedTaskBudgetReviewWidgetQuery({ variables: { projectId } });
  const { chartData, totalRemainingBudgetInCents } = useMemo(
    () => mapToChartData(data?.projectCompletedTaskBudgetReview),
    [data],
  );

  return (
    <div css={Css.w100.mw0.hPx(320).p3.bgWhite.ba.bcGray300.bw2.br8.$}>
      <div {...tid.totalLabel} css={Css.tal.xl2.pb1.$}>
        {formatCentsToPrice(totalRemainingBudgetInCents)}
      </div>
      <div css={Css.lg.gray700.pb4.df.$}>
        Completed Task Budget Review
        <div css={Css.mya.pl1.$}>
          <Tooltip
            title="The remaining unbilled budget on completed tasks, grouped by days since completion."
            placement="top"
          >
            <Icon icon="infoCircle" color={Palette.Gray600} inc={2.3} />
          </Tooltip>
        </div>
      </div>
      <div css={Css.h("55%").df.fdr.$}>
        {chartData.map(({ costInCents, bucket, dataset }, i) => (
          <div key={bucket} css={Css.w100.mw0.$} {...tid.pieChart}>
            <div css={Css.maxh("70%").h100.mb1.$}>
              <PieChart
                dataset={dataset}
                disabled={loading || totalRemainingBudgetInCents === 0}
                colors={
                  costInCents < thresholdAmountsInCents[i]
                    ? [Palette.Blue100, Palette.Blue600]
                    : [Palette.Red100, Palette.Red600]
                }
                loadingColor={[Palette.Gray200]}
                tooltipTheme="dark"
                tooltipValueFormat={(value, slice) =>
                  slice === "total"
                    ? `Remaining unbilled budget from completed tasks: ${formatCentsToPrice(value)}`
                    : `Available budget from tasks completed ${slice} ago: ${formatCentsToPrice(value)}`
                }
                emptyStateTooltipText={
                  totalRemainingBudgetInCents === 0 ? "There are no completed tasks yet." : undefined
                }
              />
            </div>
            <div css={Css.sm.mb1.gray700.$}>{formatCentsToPrice(costInCents)} to Review</div>
            <div css={Css.xs2.gray600.$}>{bucket}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

type ChartData = { bucket: string; costInCents: number; dataset: { id: string | null; value: number | boolean }[] };

function mapToChartData(data: CompletedTaskBudgetReviewWidgetQuery["projectCompletedTaskBudgetReview"] | undefined): {
  chartData: ChartData[];
  totalRemainingBudgetInCents: number;
} {
  const totalRemainingBudgetInCents = data?.sum((item) => item.costInCents);
  if (!data || !totalRemainingBudgetInCents) return { chartData: emptyDataset, totalRemainingBudgetInCents: 0 };
  const chartData = data.map((item) => ({
    ...item,
    dataset: [
      { id: "total", value: totalRemainingBudgetInCents - item.costInCents },
      { id: item.bucket, value: item.costInCents },
    ],
  }));
  return { chartData, totalRemainingBudgetInCents };
}

const emptyDataset = [
  { costInCents: 0, bucket: "0-30 days", dataset: [{ id: null, value: true }] },
  { costInCents: 0, bucket: "31-60 days", dataset: [{ id: null, value: true }] },
  { costInCents: 0, bucket: "61+ days", dataset: [{ id: null, value: true }] },
];

// These thresholds are used to determine the color of the pie chart segments
const thresholdAmountsInCents = [500_00, 1000_00, 5000_00];
